import { useState, useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import type { GeoMapVenue, GeoMapRegionSummary } from '../../../api/marketing';
import { fetchGeoMap } from '../../../api/marketing';

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN || '';

function buildVenueGeoJSON(venues: GeoMapVenue[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: venues.map((v) => {
      const fillPct = v.avgFillRate !== null ? v.avgFillRate * 100 : null;
      const available = v.totalTicketsAvailable;
      // Color: green=available, amber=low, red=nearly sold out, gray=N/A
      const color = fillPct === null ? '#9CA3AF' : fillPct >= 90 ? '#ef4444' : fillPct >= 70 ? '#f59e0b' : '#22c55e';

      return {
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [v.longitude, v.latitude] },
        properties: {
          venueKey: v.venueKey,
          name: v.venueName,
          city: v.city || '',
          state: v.state || '',
          region: v.bonsaibarRegion || '',
          upcomingEvents: v.upcomingEvents,
          capacity: v.totalCapacity,
          sold: v.totalTicketsSold,
          available,
          fillPct: fillPct !== null ? Math.round(fillPct) : null,
          color,
          radius: Math.max(6, Math.min(v.totalCapacity / 5, 20)),
        },
      };
    }),
  };
}

const LOOKAHEAD_PRESETS = [
  { label: '7 days', days: 7 },
  { label: '14 days', days: 14 },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
];

function getLookaheadRange(days: number): { startDate: string; endDate: string } {
  const start = new Date();
  const end = new Date();
  end.setDate(end.getDate() + days);
  return {
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0],
  };
}

export default function GeoCommandCenter() {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [venues, setVenues] = useState<GeoMapVenue[]>([]);
  const [regionSummaries, setRegionSummaries] = useState<GeoMapRegionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lookaheadDays, setLookaheadDays] = useState(30);

  // Fetch data when lookahead changes
  useEffect(() => {
    setLoading(true);
    setError(null);
    const range = getLookaheadRange(lookaheadDays);
    fetchGeoMap(range.startDate, range.endDate)
      .then((data) => {
        setVenues(data.venues);
        setRegionSummaries(data.regionSummaries);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [lookaheadDays]);

  // Update map source data when venues change
  useEffect(() => {
    if (mapRef.current && !loading && venues.length > 0) {
      const source = mapRef.current.getSource('venues') as mapboxgl.GeoJSONSource | undefined;
      if (source) {
        source.setData(buildVenueGeoJSON(venues));
        return; // Source updated, no need to recreate map
      }
    }
  }, [venues, loading]);

  // Initialize map (once)
  useEffect(() => {
    if (!mapContainer.current || loading || venues.length === 0) return;
    if (mapRef.current) return;

    const isDark = document.documentElement.classList.contains('dark');
    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: isDark ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/light-v11',
      center: [-98, 39], // Center of US
      zoom: 3.5,
    });

    map.addControl(new mapboxgl.NavigationControl(), 'top-right');

    map.on('load', () => {
      const geojson = buildVenueGeoJSON(venues);

      map.addSource('venues', { type: 'geojson', data: geojson });

      // Circle layer — colored by availability
      map.addLayer({
        id: 'venue-circles',
        type: 'circle',
        source: 'venues',
        paint: {
          'circle-color': ['get', 'color'],
          'circle-radius': ['get', 'radius'],
          'circle-opacity': 0.8,
          'circle-stroke-width': 1.5,
          'circle-stroke-color': '#fff',
        },
      });

      // Popups on click
      map.on('click', 'venue-circles', (e) => {
        const feature = e.features?.[0];
        if (!feature || feature.geometry.type !== 'Point') return;

        const props = feature.properties;
        const coords = feature.geometry.coordinates.slice() as [number, number];

        // Use DOM construction instead of setHTML to prevent XSS from database-sourced venue names
        const popupEl = document.createElement('div');
        popupEl.style.cssText = 'font-family: inherit; line-height: 1.5;';

        const title = document.createElement('strong');
        title.style.fontSize = '14px';
        title.textContent = props?.name || '';
        popupEl.appendChild(title);

        popupEl.appendChild(document.createElement('br'));
        const subtitle = document.createElement('span');
        subtitle.style.cssText = 'color: #666; font-size: 12px;';
        subtitle.textContent = `${props?.city || ''}, ${props?.state || ''} — ${props?.region || ''}`;
        popupEl.appendChild(subtitle);

        const hr = document.createElement('hr');
        hr.style.cssText = 'margin: 6px 0; border: none; border-top: 1px solid #eee;';
        popupEl.appendChild(hr);

        const grid = document.createElement('div');
        grid.style.cssText = 'font-size: 12px; display: grid; grid-template-columns: 1fr 1fr; gap: 4px;';
        const rows = [
          ['Events', props?.upcomingEvents],
          ['Capacity', props?.capacity],
          ['Sold', props?.sold],
          ['Available', props?.available],
          ['Fill Rate', props?.fillPct !== null ? `${props?.fillPct}%` : 'N/A'],
        ];
        for (const [label, value] of rows) {
          const labelEl = document.createElement('span');
          labelEl.textContent = label;
          grid.appendChild(labelEl);
          const valueEl = document.createElement('span');
          valueEl.style.cssText = 'text-align: right; font-weight: 600;';
          if (label === 'Available') {
            valueEl.style.color = Number(value) > 0 ? '#22c55e' : '#ef4444';
          }
          valueEl.textContent = String(value ?? '');
          grid.appendChild(valueEl);
        }
        popupEl.appendChild(grid);

        new mapboxgl.Popup({ offset: 15, maxWidth: '280px' })
          .setLngLat(coords)
          .setDOMContent(popupEl)
          .addTo(map);
      });

      // Cursor pointer on hover
      map.on('mouseenter', 'venue-circles', () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', 'venue-circles', () => {
        map.getCanvas().style.cursor = '';
      });

      // Fit bounds to venues
      if (geojson.features.length > 0) {
        const bounds = new mapboxgl.LngLatBounds();
        for (const f of geojson.features) {
          if (f.geometry.type === 'Point') {
            bounds.extend(f.geometry.coordinates as [number, number]);
          }
        }
        map.fitBounds(bounds, { padding: 60, maxZoom: 12 });
      }
    });

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [loading, venues]);

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-lg font-semibold text-bark-900 dark:text-stone-100">Geographic Command Center</h2>
        <div className="flex items-center gap-4">
          {/* Lookahead selector */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-stone-500 dark:text-stone-400">Next</span>
            {LOOKAHEAD_PRESETS.map((preset) => (
              <button
                key={preset.days}
                onClick={() => setLookaheadDays(preset.days)}
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition ${
                  lookaheadDays === preset.days
                    ? 'bg-moss-600 text-white'
                    : 'bg-stone-100 dark:bg-[#242d28] text-stone-600 dark:text-stone-400 hover:bg-stone-200 dark:hover:bg-[#2a352e]'
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>
          {/* Legend */}
          <div className="flex items-center gap-3 text-xs text-stone-500">
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-full bg-green-500" /> Available
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-full bg-amber-500" /> Low ({'>'}70%)
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-full bg-red-500" /> Sold Out
            </span>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-[#1a1f1c] rounded-lg border border-stone-200 dark:border-stone-700 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-[500px]">
            <div className="w-8 h-8 border-2 border-moss-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div ref={mapContainer} style={{ height: '500px', width: '100%' }} />
        )}
      </div>

      {/* Region summary cards below map */}
      {regionSummaries.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {regionSummaries.map((r) => (
            <div
              key={r.bonsaibarRegion}
              className="bg-white dark:bg-[#1a1f1c] rounded-lg border border-stone-200 dark:border-stone-700 p-3"
            >
              <p className="text-xs font-medium text-bark-900 dark:text-stone-200 truncate">{r.bonsaibarRegion}</p>
              <div className="mt-2 space-y-1 text-xs text-stone-500 dark:text-stone-400">
                <div className="flex justify-between">
                  <span>Spend</span>
                  <span className="font-medium text-bark-900 dark:text-stone-200">
                    {r.totalSpend >= 1000 ? `$${(r.totalSpend / 1000).toFixed(1)}k` : `$${r.totalSpend.toFixed(0)}`}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>ROAS</span>
                  <span className={`font-medium ${
                    r.roas !== null && r.roas >= 3 ? 'text-green-600' : r.roas !== null && r.roas >= 1 ? 'text-amber-600' : 'text-red-500'
                  }`}>
                    {r.roas !== null ? `${r.roas.toFixed(1)}x` : '--'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Available</span>
                  <span className="font-medium text-bark-900 dark:text-stone-200">{r.totalTicketsAvailable}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
